import { FC, useContext, useEffect, useMemo, useState } from "react";
import { ColumnConfig } from "@app/shared/waffles";
import { Waffles } from "@app/shared/waffles/Waffles";
import { FlyoutContext, FlyoutFooter } from "@app/shared/Flyouts";
import ExceptionsService from "@app/exceptions/exceptionsService";
import { SpecialtyGroupExceptionDisplay } from "@app/exceptions/interfaces";
import { formatDateTime } from "@app/shared/formatDateTime";
import LoadingSpinner from "@app/shared/LoadingSpinner";
import { QESButton } from "@app/shared/qes-standards/Button";
import { QESAlert } from "@app/shared/qes-standards/Alert";
import { capitalizeFirstLetter } from "@app/shared/utility/capitalizeFirstLetter";
import { truncateStringWithEllipsis } from "@app/shared/utility/truncateStringWithEllipsis";


interface UnprocessedSpecialtyGroupExceptionsProps {
  isZipProject?: boolean;
  onSubmit?: () => void;
  projectId: number;
}

interface PendingWaffleToppings {
  key: string;
  actionBy: string;
  actionDate: string;
  comment: string;
  county: string;
  specialtyGroup: string;
  status: string;
}

const PendingUnprocessedSpecialtyGroupExceptionsChanges: FC<React.PropsWithChildren<UnprocessedSpecialtyGroupExceptionsProps>> = (props: UnprocessedSpecialtyGroupExceptionsProps) => {
  const specialtyGroupExceptionsService = new ExceptionsService();
  const [unprocessedSpecialtyGroupExceptions, setUnprocessedSpecialtyGroupExceptions] = useState<SpecialtyGroupExceptionDisplay[]>();
  const [error, setError] = useState(false);
  const [loading, setLoading] = useState(false);
  const flyoutContext = useContext(FlyoutContext);

  useEffect(() => {
    setLoading(true);

    specialtyGroupExceptionsService.getUnprocessedSpecialtyGroupExceptionsByProjectId(props.projectId)
      .then((result) => {
        setUnprocessedSpecialtyGroupExceptions(result);
      }, () => {
        setError(true);
      });

    setLoading(false);
  }, []);

  const columnConfig = useMemo<ColumnConfig>(() => {
    return {
      actionBy: {
        displayText: "Action By",
        noSort: true,
        testId: "action-by",
      },
      actionDate: {
        displayText: "Action Date",
        noSort: true,
        testId: "action-date",
      },
      comment: {
        displayText: "Comment",
        noSort: true,
        testId: "comment",
      },
      county: {
        displayText: "County",
        hidden: props.isZipProject,
        noSort: true,
        testId: "county",
      },
      exceptionNarrative: {
        displayText: "Exception Narrative",
        noSort: true,
        testId: "exception-narrative",
      },
      specialtyGroup: {
        displayText: "Specialty Group",
        noSort: true,
        testId: "specialty-group",
      },
      status: {
        displayText: "Status",
        noSort: true,
        testId: "status",
      },
      zipCode: {
        displayText: "Zip Code",
        hidden: !props.isZipProject,
        noSort: true,
        testId: "zip-code",
      }
    };
  }, [props.isZipProject]);

  const renderLoadedContent = () => {
    if (unprocessedSpecialtyGroupExceptions.length > 0) {
      return (
        <>
          {renderUnprocessedSpecialtyGroupExceptions(unprocessedSpecialtyGroupExceptions, columnConfig)}
          <FlyoutFooter
            submitButton={<QESButton qesType="primary" qesTestId="pending-providers__process" onClick={() => submit()}>Process Specialty Group Exceptions</QESButton>}
          />
        </>
      );
    } else {
      return renderNoResults();
    }
  };

  const submit = () => {
    specialtyGroupExceptionsService.processUnprocessedSpecialtyGroupExceptions(props.projectId);
    if (props.onSubmit) {
      props.onSubmit();
    }
    flyoutContext.handleCloseFlyout();
  }

  return (
    <>
      {error
        ? <QESAlert type="danger">Something went wrong on our end.</QESAlert>
        : !!unprocessedSpecialtyGroupExceptions && !loading ?
          renderLoadedContent() : <LoadingSpinner />}
    </>
  );
};

const renderUnprocessedSpecialtyGroupExceptions = (unprocessedSpecialtyGroupExceptions: SpecialtyGroupExceptionDisplay[], columnConfig: ColumnConfig) => {

  const commentAndNarrativeLength = 25;

  /* oxlint-disable sort-keys */
  const data: PendingWaffleToppings[] = unprocessedSpecialtyGroupExceptions.map((usge) => (
    {
      key: usge.specialtyGroupExceptionId.toString(),
      actionBy: usge.updatedByUserFullName,
      actionDate: formatDateTime(usge.dateUpdated),
      comment: truncateStringWithEllipsis(usge.comment, commentAndNarrativeLength),
      exceptionNarrative: truncateStringWithEllipsis(usge.exceptionNarrative, commentAndNarrativeLength),
      zipCode: `${usge.regionSetDescription}, ${usge.regionSetStateName}`,
      county: `${usge.regionSetDescription}, ${usge.regionSetStateName}`,
      specialtyGroup: usge.specialtyGroupCode,
      status: capitalizeFirstLetter(usge.specialtyGroupExceptionStatus),
    }
  ));
  /* oxlint-enable sort-keys */

  return (
    <Waffles<PendingWaffleToppings>
      columns={columnConfig}
      data={data}
      testId="pending-exceptions"
    />
  );
};

const renderNoResults = () => {
  return (<QESAlert type="danger">No unprocessed specialty group exceptions found.</QESAlert>);
};

export { PendingUnprocessedSpecialtyGroupExceptionsChanges };
